import { Check, Star } from "lucide-react";
import { Button } from "../components/ui/button";

const plans = [
  {
    name: "Starter Pack",
    price: "£97",
    period: "one-time",
    tagline: "Pass your EHO inspection without stress — even if you've got nothing in place.",
    features: [
      "HACCP documentation",
      "Allergen matrix",
      "PDF download pack",
    ],
    cta: "Get Started",
    highlight: false,
    label: null,
  },
  {
    name: "Audit + Action Plan",
    price: "£247",
    period: "one-time",
    tagline: "Clear step-by-step plan to reach a 4-5 hygiene rating fast.",
    features: [
      "Printed compliance pack",
      "On-site audit",
      "Risk identification",
      "Personalised action plan",
    ],
    cta: "Choose This Plan",
    highlight: true,
    label: "Most Popular",
  },
  {
    name: "Tailored HACCP System",
    price: "£797",
    period: "one-time",
    tagline: "Fully customised HACCP system built for your exact kitchen and menu.",
    features: [
      "Staff training pack",
      "Business-specific CCPs",
      "EHO-ready system",
      "Allergen documentation",
    ],
    cta: "Choose This Plan",
    highlight: false,
    label: "Advanced",
  },
  {
    name: "Done-For-You EHO Pass",
    price: "£1,597",
    period: "one-time",
    tagline: "We handle everything — you just run your business.",
    features: [
      "Full customised HACCP build",
      "Staff training included",
      "All paperwork completed",
      "Allergen system setup",
      "3 months priority support",
    ],
    cta: "Get Full Service",
    highlight: false,
    label: "Premium",
  },
];

const addons = [
  {
    name: "Monthly Support",
    price: "£97 – £197",
    period: "/month",
    features: ["Monthly compliance checks", "Priority support", "Emergency EHO visit prep", "Ongoing EHO readiness"],
  },
  {
    name: "Staff Training Day",
    price: "£297",
    period: "per day",
    features: ["Full-day on-site training", "Practical hands-on guidance", "Staff Q&A session"],
  },
];

export default function Pricing() {
  return (
    <section
      data-testid="pricing-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#0a0a0a]/[0.93]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="gold-line mx-auto mb-6" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
            Elite Service Packages
          </span>
          <h2
            data-testid="pricing-heading"
            className="font-heading text-3xl md:text-4xl font-medium text-white mt-4"
          >
            Invest in Your
            <br />
            <span className="text-zinc-500">Compliance Confidence</span>
          </h2>
        </div>

        {/* Main pricing grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan) => (
            <div
              key={plan.name}
              data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`rounded-lg p-7 flex flex-col relative ${
                plan.highlight
                  ? "highlight-plan bg-[#c9a84c]/[0.04]"
                  : "dark-card"
              }`}
            >
              {plan.label && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${
                    plan.highlight
                      ? "bg-gold text-black"
                      : "bg-white/10 text-zinc-300"
                  }`}>
                    {plan.label}
                  </span>
                </div>
              )}

              <h3 className="font-heading text-lg font-medium text-white mt-1">
                {plan.name}
              </h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-3xl font-semibold text-gold">
                  {plan.price}
                </span>
                <span className="text-xs text-zinc-500">/ {plan.period}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{plan.tagline}</p>

              <div className="my-5 border-t border-white/5" />

              <ul className="space-y-2.5 flex-1">
                {plan.features.map((feat) => (
                  <li key={feat} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check size={14} className="text-gold flex-shrink-0" />
                    {feat}
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
                asChild
                className={`mt-6 w-full py-2.5 text-sm font-semibold rounded transition-colors ${
                  plan.highlight
                    ? "bg-gold text-black hover:bg-[#b8963f]"
                    : "border border-[#c9a84c]/25 bg-transparent text-gold hover:bg-[#c9a84c]/10"
                }`}
              >
                <a href="#contact">{plan.cta}</a>
              </Button>
            </div>
          ))}
        </div>

        {/* Add-ons */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {addons.map((addon) => (
            <div
              key={addon.name}
              data-testid={`addon-card-${addon.name.toLowerCase().replace(/\s/g, '-')}`}
              className="dark-card p-7 flex flex-col sm:flex-row items-start gap-4 sm:gap-6"
            >
              <div className="flex-shrink-0">
                <Star size={20} className="text-gold" />
              </div>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
                  <h4 className="font-heading text-lg font-medium text-white">{addon.name}</h4>
                  <span className="text-gold font-heading text-xl font-semibold whitespace-nowrap">
                    {addon.price}<span className="text-xs text-zinc-500 ml-1">{addon.period}</span>
                  </span>
                </div>
                <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  {addon.features.map((f) => (
                    <li key={f} className="flex items-center gap-1.5 text-xs text-zinc-400">
                      <Check size={12} className="text-gold" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
